import { create } from 'zustand';
import { Part, FlangeId, EdgeKey, MaterialKey, Flange, Hole, HoleId, DimMode } from './lib/types';
import {
  defaultPart, addFlange, deleteFlange, updateFlange, updateAttachment, setMaterial, setThickness,
  applyPreset, PresetKind, addHole, deleteHole, updateHole, changeDimMode,
} from './lib/part';
import { serializePart, parsePartJson, downloadJson, pickJsonFile } from './lib/io';

const HISTORY_LIMIT = 80;

interface AppState {
  part: Part;
  past: Part[];
  future: Part[];
  selectedId: FlangeId | null;
  showFlat: boolean;

  // selection / view
  select: (id: FlangeId | null) => void;
  toggleFlat: () => void;

  // mutations
  addChild: (parentId: FlangeId, edge: EdgeKey, opts?: Parameters<typeof addFlange>[3]) => FlangeId | null;
  applyPreset: (parentId: FlangeId, edge: EdgeKey, preset: PresetKind) => FlangeId | null;
  remove: (id: FlangeId) => void;
  patchFlange: (id: FlangeId, patch: Partial<Flange>) => void;
  patchAttachment: (id: FlangeId, patch: Partial<NonNullable<Flange['attachment']>>) => void;
  setMat: (m: MaterialKey) => void;
  setThickness: (t: number) => void;
  setKFactor: (k: number) => void;
  setMiterGap: (g: number) => void;
  setDimMode: (m: DimMode) => void;
  // Increments to trigger an auto-fit of the 3D camera to the current part.
  fitVersion: number;
  bumpFit: () => void;
  // UI theme — applied as a class on <html>.
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  addHole: (flangeId: FlangeId, opts?: Partial<Omit<Hole, 'id' | 'flangeId'>>) => HoleId | null;
  removeHole: (holeId: HoleId) => void;
  patchHole: (holeId: HoleId, patch: Partial<Hole>) => void;
  reset: () => void;

  // history
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // io
  saveJson: () => void;
  loadJson: () => Promise<void>;
}

export const useApp = create<AppState>((set, get) => {
  // Helper: commit a transformation that yields a new Part. Pushes the previous
  // state to history (capped) and clears the redo stack. If `next === current`,
  // does nothing.
  const commit = (
    fn: (current: Part) => Part,
    extra?: Partial<Pick<AppState, 'selectedId'>>,
  ): boolean => {
    const before = get().part;
    const after = fn(before);
    if (after === before) return false;
    set((s) => ({
      part: after,
      past: [...s.past, before].slice(-HISTORY_LIMIT),
      future: [],
      ...(extra ?? {}),
    }));
    return true;
  };

  return {
    part: defaultPart('mild'),
    past: [],
    future: [],
    selectedId: null,
    showFlat: false,
    fitVersion: 0,
    bumpFit: () => set((s) => ({ fitVersion: s.fitVersion + 1 })),
    theme: ((typeof window !== 'undefined' && window.localStorage?.getItem('bsc.theme')) === 'dark' ? 'dark' : 'light'),
    toggleTheme: () => set((s) => {
      const next = s.theme === 'light' ? 'dark' : 'light';
      try { window.localStorage?.setItem('bsc.theme', next); } catch { /* ignore */ }
      return { theme: next };
    }),
    select: (selectedId) => set({ selectedId }),
    toggleFlat: () => set((s) => ({ showFlat: !s.showFlat })),

    addChild: (parentId, edge, opts) => {
      const before = get().part;
      const after = addFlange(before, parentId, edge, opts);
      if (after === before) return null;
      const newIds = Object.keys(after.flanges).filter((k) => !(k in before.flanges));
      const id = newIds[0] ?? null;
      set((s) => ({
        part: after,
        past: [...s.past, before].slice(-HISTORY_LIMIT),
        future: [],
        selectedId: id,
      }));
      return id;
    },

    applyPreset: (parentId, edge, preset) => {
      const before = get().part;
      const result = applyPreset(before, parentId, edge, preset);
      if (result.added.length === 0) return null;
      const last = result.added[result.added.length - 1];
      set((s) => ({
        part: result.part,
        past: [...s.past, before].slice(-HISTORY_LIMIT),
        future: [],
        selectedId: last,
      }));
      return last;
    },

    remove: (id) => {
      commit((p) => deleteFlange(p, id), {
        selectedId: get().selectedId === id ? null : get().selectedId,
      });
    },

    patchFlange: (id, patch) => commit((p) => updateFlange(p, id, patch)) && undefined,
    patchAttachment: (id, patch) => commit((p) => updateAttachment(p, id, patch)) && undefined,

    setMat: (m) => commit((p) => setMaterial(p, m)) && undefined,
    setThickness: (t) => commit((p) => setThickness(p, t)) && undefined,
    setKFactor: (k) => commit((p) => ({ ...p, kFactor: Math.max(0, Math.min(1, k)) })) && undefined,
    setMiterGap: (g) => commit((p) => ({ ...p, miterGap: Math.max(0, g) })) && undefined,
    // Dim-mode toggle is a UI preference; we still push to history so undo restores
    // the user's last view convention along with any edits they made in that mode.
    setDimMode: (m) => commit((p) => changeDimMode(p, m)) && undefined,

    addHole: (flangeId, opts) => {
      const before = get().part;
      const after = addHole(before, flangeId, opts);
      if (after === before) return null;
      const newHole = after.holes.find((h) => !before.holes.some((b) => b.id === h.id));
      const id = newHole?.id ?? null;
      set((s) => ({
        part: after,
        past: [...s.past, before].slice(-HISTORY_LIMIT),
        future: [],
      }));
      return id;
    },
    removeHole: (holeId) => commit((p) => deleteHole(p, holeId)) && undefined,
    patchHole: (holeId, patch) => commit((p) => updateHole(p, holeId, patch)) && undefined,

    reset: () => {
      commit((p) => defaultPart(p.material), { selectedId: null });
    },

    undo: () => {
      const { past, part, future } = get();
      if (past.length === 0) return;
      const prev = past[past.length - 1];
      set({
        part: prev,
        past: past.slice(0, -1),
        future: [...future, part].slice(-HISTORY_LIMIT),
      });
    },
    redo: () => {
      const { past, part, future } = get();
      if (future.length === 0) return;
      const next = future[future.length - 1];
      set({
        part: next,
        past: [...past, part].slice(-HISTORY_LIMIT),
        future: future.slice(0, -1),
      });
    },
    canUndo: () => get().past.length > 0,
    canRedo: () => get().future.length > 0,

    saveJson: () => {
      const { part } = get();
      const text = serializePart(part);
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      downloadJson(`bendsteelcal_${ts}.json`, text);
    },

    loadJson: async () => {
      const text = await pickJsonFile();
      const part = parsePartJson(text);
      const before = get().part;
      set((s) => ({
        part,
        past: [...s.past, before].slice(-HISTORY_LIMIT),
        future: [],
        selectedId: null,
      }));
    },
  };
});
